import { useEffect, useRef } from 'react';
import { IConnectionRequest } from '../interfaces/IChatUser';
import { createChatHubService } from '../services/chatHubService';

interface UseChatHubOptions {
    token: string;
    onMessageReceived?: (userId: string, message: string, conversationId: string | null, messageId: string | null, createdAtIso?: string) => void;
    onNewConnectionRequest?: (request: IConnectionRequest) => void;
    onUserOnline?: (userId: string) => void;
    onUserOffline?: (userId: string) => void;
    onUserTyping?: (userId: string, conversationId?: string | null) => void;
    onUserStoppedTyping?: (userId: string, conversationId?: string | null) => void;
    onMessagesRead?: (conversationId: string, readerUserId: string, lastReadMessageId?: string) => void;
    onMessageReactionChanged?: (payload: { messageId: string; userId: string; action: string; reactionType: string; counts: Record<string, number> }) => void;
    onConnected?: () => void;
    onDisconnected?: () => void;
    onConnectionAccepted?: (userId: string) => void;
    onConnectionRejected?: (userId: string) => void;
    onConnectionCanceled?: (userId: string) => void;
    onConnectionRemoved?: (userId: string) => void;
}

export function useChatHub(options: UseChatHubOptions): void {
    const hubRef = useRef<ReturnType<typeof createChatHubService> | null>(null);

    useEffect(() => {
        if (!options.token) return;


        if (!hubRef.current) {
            hubRef.current = createChatHubService(options.token);
        }

        const hub = hubRef.current;
        let isMounted = true;
        window.chatHub = hub;


        const startConnection = async () => {
            try {
                await hub.start();
                if (isMounted && options.onConnected) options.onConnected();

                // Add debug helpers to window
                (window as any).chatDebug = {
                    getConnectionInfo: async () => {
                        const info = await hub.getConnectionDebugInfo();
                        console.table(info);
                        return info;
                    },
                    getOnlineUsers: async () => {
                        const users = await hub.getOnlineUsers();
                        console.log('Online users:', users);
                        return users;
                    }
                };
            } catch (err) {
                console.error('SignalR start error:', err);
            }
        };

        startConnection();

        const unsubscribers: Array<() => void> = [];
        if (options.onMessageReceived) unsubscribers.push(hub.on('ReceiveMessage', options.onMessageReceived));
        if (options.onNewConnectionRequest) unsubscribers.push(hub.on('NewConnectionRequest', options.onNewConnectionRequest));
        if (options.onUserOnline) unsubscribers.push(hub.on('UserOnline', options.onUserOnline));
        if (options.onUserOffline) unsubscribers.push(hub.on('UserOffline', options.onUserOffline));
        if (options.onUserTyping) unsubscribers.push(hub.on('UserTyping', options.onUserTyping));
        if (options.onUserStoppedTyping) unsubscribers.push(hub.on('UserStoppedTyping', options.onUserStoppedTyping));
        if (options.onMessagesRead) unsubscribers.push(hub.on('MessagesRead', options.onMessagesRead));
        if (options.onMessageReactionChanged) unsubscribers.push(hub.on('MessageReactionChanged', options.onMessageReactionChanged));
        if (options.onConnectionAccepted) unsubscribers.push(hub.on('ConnectionAccepted', options.onConnectionAccepted));
        if (options.onConnectionRejected) unsubscribers.push(hub.on('ConnectionRejected', options.onConnectionRejected));
        if (options.onConnectionCanceled) unsubscribers.push(hub.on('ConnectionCanceled', options.onConnectionCanceled));
        if (options.onConnectionRemoved) unsubscribers.push(hub.on('ConnectionRemoved', options.onConnectionRemoved));

        hub.getConnection().onclose(() => {
            if (isMounted && options.onDisconnected) options.onDisconnected();
        });

        // When SignalR auto-reconnects, re-run onConnected to allow callers to refresh snapshots
        hub.getConnection().onreconnected(() => {
            if (isMounted && options.onConnected) options.onConnected();
        });

        return () => {
            isMounted = false;
            unsubscribers.forEach((unsubscribe) => unsubscribe());
            hub.stop();
            hubRef.current = null;
        };
    }, [options.token]);

}